/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { FormattedCommaSeparatedValue } from "@MasterBluePrint/Services/FormattedValueService";
import resetMultipleSaleQuantity from "@MasterBluePrint/Store/Components/ProductDetails/Handlers/ResetMultipleSaleQuantity";
import { CustomApplicationState } from "@MasterBluePrint/Store/CustomApplicationState";
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
}

const mapStateToProps = (
    state: CustomApplicationState,
    props: { configurationCompleted?: boolean; variantSelectionCompleted?: boolean } & HasProductContext,
) => {
    return {
        productSettings: getSettingsCollection(state).productSettings,
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        multipleSaleQty: props.productContext.product?.detail?.multipleSaleQty,
        product: props.productContext,
        isResetMultipleSaleQuantity: state.resetMultipleSaleQuantityState.resetMultipleSaleQuantity,
    };
};

const mapDispatchToProps = {
    resetMultipleSaleQuantity,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
            }

            input:not(.react-datetime-picker__inputGroup__input),
            select:not(.react-datetime-picker__inputGroup__input),
            textarea,
            .mobiusFileUpload,
            .react-datetime-picker {
                :focus-visible {
                    outline-color: #2060AD !important;
                }
                @media (max-width: 767px) {
                    padding-right:unset !important;
                }
                @media screen and (max-width: 1080px) and (min-width: 1024px) {
                    padding-right:unset !important;
                }

        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
            width: 9rem !important;
            text-align: center;
            padding: 0;
            background: #FFF !important;
            font-size:1rem !important;
            color:#2060AD !important;
            opacity: 1;
            &:focus:focus-visible {
                width: 9rem !important;
                border: 1px solid #2060AD;
                -webkit-box-shadow:0px 0px 17px 6px rgba(195,212,234,.31);
                -moz-box-shadow: 0px 0px 17px 6px rgba(195,212,234,.31);
                box-shadow: 0px 0px 17px 6px rgba(195,212,234,.31);.:focus:focus-visible
        `,
    },

};

const ProductQuantityOrdered = ({
    productSettings,
    hasEnoughInventory,
    productContext: {
        product: { id: productId },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    variantSelectionCompleted,
    multipleSaleQty,
    product,
    isResetMultipleSaleQuantity,
    resetMultipleSaleQuantity,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current) {
            const qtyContainer = ref.current.closest('.qty-container-new');
            const qtyWrapper = qtyContainer?.parentElement;
            qtyWrapper?.parentElement?.classList.add('qty-wrapper');
        }
    }, []);

    useEffect(() => {
        if (multipleSaleQty && multipleSaleQty > 1) {
            setProductQty(multipleSaleQty.toString());
            qtyOrdered = multipleSaleQty;
            onQtyOrderedChanged!(multipleSaleQty);
        }
    }, []);

    useEffect(() => {
        if (isResetMultipleSaleQuantity && multipleSaleQty && multipleSaleQty > 1) {
            setProductQty(multipleSaleQty.toString());
            qtyOrdered = multipleSaleQty;
            onQtyOrderedChanged!(multipleSaleQty);
            resetMultipleSaleQuantity({ reset: false });
        }
    }, [isResetMultipleSaleQuantity]);

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
        onQtyOrderedChanged!(qtyOrdered);
    }, [qtyOrdered]);

    if (!productSettings.canAddToCart || !hasEnoughInventory || !canAddToCart) {
        return (
            <StyledWrapper className=" qty-container-new" />
        );
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === ".") {
            event.preventDefault();
        }

        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.replace(/^0+/, '');

        if ((product.product.properties.condition.toLowerCase() === "open box" || product.product.properties.condition.toLowerCase() === "used") && (product.productInfo.inventory?.qtyOnHand !== undefined && Number(value) > product.productInfo.inventory?.qtyOnHand)) {
            toasterContext.addToast({ body: siteMessage("Core_NotEnoughStockError"), messageType: "danger" });
            return;
        }

        setProductQty(FormattedCommaSeparatedValue(value));

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };
    const increaseOrderedProductQty = () => {
        const amount = Number(productQty) + 1;

        if ((product.product.properties.condition.toLowerCase() === "open box" || product.product.properties.condition.toLowerCase() === "used") && (product.productInfo.inventory?.qtyOnHand !== undefined && amount > product.productInfo.inventory?.qtyOnHand)) {
            toasterContext.addToast({ body: siteMessage("Core_NotEnoughStockError"), messageType: "danger" });
            return;
        }

        setProductQty(FormattedCommaSeparatedValue(amount.toString()));

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(amount);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };
    const decreaseOrderedProductQty = () => {
        const amount = Number(productQty) > 1 ? Number(productQty) - 1 : Number(productQty);

        setProductQty(FormattedCommaSeparatedValue(amount.toString()));

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(amount);
        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };
    return (
        <StyledWrapper
            style={{ display: "block", width: "100%", marginTop: "1rem", marginBottom: "1rem" }}
            className="qty-container qty-container-new"
            ref={ref}
        >
            <StyledWrapper style={{ padding: "0" }}>
                <StyledWrapper style={{ position: "relative" }} className="input-container">
                    <TextField
                        label={labelOverride ?? translate("Qty:")}
                        value={FormattedCommaSeparatedValue(productQty)}
                        id="orderedQtyInput"
                        type="text"
                        min={0}
                        max={999999}
                        data-test-selector="product_qtyOrdered"
                        {...otherProps}
                        onKeyPress={keyPressHandler}
                        onChange={qtyChangeHandler}
                        {...styles}
                    />
                    <Clickable
                        style={{
                            position: "absolute",
                            top: "2.7rem",
                            left: ".1rem",
                            height: "38px",
                            background: "rgba(104, 172, 247, 8%)",
                            padding: ".625rem",
                            fontSize: "1.4rem",
                            color: "#002554",
                            borderBottomLeftRadius: "2",
                            borderTopLeftRadius: "2",
                        }}
                        onClick={() => decreaseOrderedProductQty()}
                        aria-label="Decrease product quantity"
                    >
                        <Icon src="Minus" />
                    </Clickable>

                    <Clickable
                        onClick={() => increaseOrderedProductQty()}
                        style={{
                            position: "absolute",
                            top: "2.7rem",
                            right: ".08rem",
                            height: "38px",
                            background: "rgba(104, 172, 247, 8%)",
                            padding: ".625rem",
                            fontSize: "1.4rem",
                            color: "#002554",
                            borderBottomRightRadius: "2",
                            borderTopRightRadius: "2",
                        }}
                        aria-label="Increase product quantity"
                    >
                        <Icon src="Plus" />
                    </Clickable>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductQuantityOrdered));